﻿import { PublishingHouseModel } from 'ApiClient/metadata/types/PublishingHouseModel'
import { PublishingHouse } from 'Product/models/ProductModel'
import { claimTypesEnum } from 'User/utils/claims'
import filter from 'lodash/fp/filter'
import flow from 'lodash/fp/flow'
import keys from 'lodash/fp/keys'
import pickBy from 'lodash/fp/pickBy'

import { Authentication } from 'Common/auth/types'
import { ArrayDistinct, IsDefined } from 'Common/utils/helpers/HelperGeneral'

import { ClaimType } from '../../types'

export const getMyPublishingHouseIds = (auth: Authentication, claimType: ClaimType): any =>
    flow(
        keys,
        filter((key) => key !== 'private'),
    )(auth.data?.opus_user?.access?.[claimType] ?? {})

export const filterPublishingHousesByMyAccess = <T extends { id?: string | null }>(
    publishingHouses: T[] | null | undefined,
    auth: Authentication,
    claimType: ClaimType,
): T[] => {
    if (!publishingHouses) return []
    const myPublishingHouseIds: string[] = getMyPublishingHouseIds(auth, claimType)
    return publishingHouses.filter((ph) => ph.id != null && myPublishingHouseIds.includes(ph.id))
}

export const getMyPublishingHouseIdsApprover = (auth: Authentication, module: ClaimType): any =>
    flow(
        pickBy((claims: any) => claims.includes('approver')),
        keys,
        filter((key) => key !== 'private'),
    )(auth.data?.opus_user?.access?.[module] ?? {})

const getPublishingHouseFromClaimValue = (claimValue: string) => {
    const [, groupAndHouse] = claimValue.split('|')
    const [, publishingHouse] = groupAndHouse.split(':')
    return publishingHouse
}

export const getPublishingHouseGroups = (auth: Authentication) =>
    auth.data?.opus_user.publishingHouseGroups ?? []

export const getMyUserId = (auth: Authentication) => auth.data?.opus_user_id ?? null
export const getMyContactIds = (auth: Authentication) => auth.data?.opus_contact_id ?? []

type PublishingHouseScopedMetadata = {
    publishingHouseId?: string | null
    publishingHouseIds?: string[] | null
    publishingHouses?: string[] | string | null
}

const metadataKeysWhereHousesAreMembership = [
    'publishingHouseGroups',
    'publishingHouseGroupsWithHouses',
]

const toLowerIds = (...values: unknown[]): string[] =>
    values
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .map((value) => value.toLowerCase())

const metadataHouseIds = (item: unknown): string[] => {
    if (!item || typeof item !== 'object') return []
    const { publishingHouseId, publishingHouseIds, publishingHouses } =
        item as PublishingHouseScopedMetadata
    return toLowerIds(publishingHouseId, publishingHouseIds, publishingHouses)
}

const dedupeMetadataById = <T>(items: T[]): T[] => {
    const seen = new Set<string>()
    return items.filter((item) => {
        const id = (item as { id?: unknown })?.id
        if (typeof id !== 'string' && typeof id !== 'number') return true
        const key = String(id).toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

export const filterMetadataByMyPublishingHouses = <T>(
    items: T[] | null | undefined,
    myPublishingHouseIds: string[],
): T[] => {
    if (!items) return []
    if (myPublishingHouseIds.length === 0) return items
    const mine = ArrayDistinct(toLowerIds(myPublishingHouseIds))
    return dedupeMetadataById(
        items.filter((item) => {
            const houseIds = metadataHouseIds(item)
            return houseIds.length === 0 || houseIds.some((id) => mine.includes(id))
        }),
    )
}

export const filterMetadataMapByMyPublishingHouses = <T extends object>(
    metadata: T,
    myPublishingHouseIds: string[],
): T => {
    if (myPublishingHouseIds.length === 0) return metadata
    const next: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(metadata)) {
        next[key] =
            Array.isArray(value) && !metadataKeysWhereHousesAreMembership.includes(key)
                ? filterMetadataByMyPublishingHouses(value, myPublishingHouseIds)
                : value
    }
    return next as T
}

const allDefined = (...inputs: unknown[]) => inputs.every(IsDefined)

export const hasClaimWithValue = (
    auth: Authentication,
    claimName: ClaimType,
    claimValue: string,
): boolean =>
    allDefined(claimName, claimValue) &&
    (auth?.data?.opus_user?.claims ?? []).some(
        (claim) => claim.name === claimName && claim.value === claimValue,
    )

export const hasClaimWithValueForScope = (
    auth: Authentication,
    claimName: ClaimType,
    claimValue: string,
    publishingHouse: PublishingHouseModel,
) => {
    const possibleValues = [
        `${claimValue}|ph:${publishingHouse.id}`,
        `${claimValue}|phg:${publishingHouse.publishingHouseGroupId}`,
    ]

    return (auth.data?.opus_user?.claims ?? []).some(
        (claim) => claim.name === claimName && possibleValues.includes(claim.value),
    )
}

export function hasWorkCalculationClaim(
    auth: Authentication,
    publishingHouse: PublishingHouse,
): boolean {
    return (
        hasClaimWithValueForScope(
            auth,
            claimTypesEnum.enum.opus_work,
            'calculations',
            publishingHouse,
        ) &&
        hasClaimWithValueForScope(auth, claimTypesEnum.enum.opus_work, 'royalty', publishingHouse)
    )
}

export const isCustomerAdmin = (auth: Authentication): boolean => {
    return (
        hasClaimWithValue(auth, 'opus_user', 'admin') ||
        hasClaimWithValue(auth, 'opus_user', 'customeradmin')
    )
}

export const hasIsbnRights = (auth: Authentication, publishingHouse: PublishingHouse): boolean => {
    return hasClaimWithValueForScope(auth, claimTypesEnum.enum.opus_work, 'isbn', publishingHouse)
}

export const hasPublishRights = (
    auth: Authentication,
    publishingHouse: PublishingHouse,
): boolean => {
    return hasClaimWithValueForScope(
        auth,
        claimTypesEnum.enum.opus_work,
        'publisher',
        publishingHouse,
    )
}

export const isOpusAdmin = (auth: Authentication) =>
    ['opus_user', 'opus_metadata'].some((claim: any) => hasClaimWithValue(auth, claim, 'admin'))

export const isContactPowerUser = (auth: Authentication) =>
    [claimTypesEnum.enum.opus_contacts].some((claim: any) =>
        hasClaimWithBaseValue(auth, claim, 'power_user'),
    )

export const isWorkPowerUser = (auth: Authentication) =>
    [claimTypesEnum.enum.opus_work].some((claim: any) =>
        hasClaimWithBaseValue(auth, claim, 'power_user'),
    )

export const hasClaim = (auth: Authentication, claimName: ClaimType): boolean =>
    IsDefined(claimName) &&
    (auth.data?.opus_user?.claims ?? []).some((claim) => claim.name === claimName)

const getClaimValueStart = (claimValue: string) => {
    const [claimvalueStart] = claimValue.split('|')
    return claimvalueStart
}

export const hasClaimWithBaseValue = (
    auth?: Authentication,
    claimName?: ClaimType,
    claimValueStartWith?: any,
) => {
    if (!auth || !claimValueStartWith) return false
    return (
        IsDefined(claimName) &&
        (auth.data?.opus_user?.claims ?? []).some(
            (claim) =>
                claim.name === claimName && getClaimValueStart(claim.value) === claimValueStartWith,
        )
    )
}

export const hasClaimContainingValue = (
    auth: Authentication,
    claimName: ClaimType,
    claimValue: string,
): boolean =>
    allDefined(claimName, claimValue) &&
    (auth.data?.opus_user?.claims ?? []).some(
        (claim) => claim.name === claimName && claim.value.includes(claimValue),
    )

export const getUserClaimsByBaseValue = (auth: Authentication, baseValue: string) =>
    (auth.data?.opus_user?.claims ?? []).filter(
        ({ value }) => getClaimValueStart(value) === baseValue,
    )

export const getDistinctPublishingHousesFromClaims = (claims: { value: string }[]) =>
    ArrayDistinct(claims.map(({ value }) => getPublishingHouseFromClaimValue(value)))
